import React, { useEffect, useState } from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const TrendingProduct = ({ image, type }) => {
  return (
    <div className="flex flex-col items-center justify-center text-center p-4 outline-none">
      {/* Larger Square Image */}
      <div className="w-64 h-64 overflow-hidden rounded-xl shadow-md border border-gray-100 group hover:border-[#D4AF37]/50 transition-all duration-300">
        <img
          src={image}
          alt="Product"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      {/* Type Badge */}
      <span
        className={`px-4 py-1.5 mt-5 rounded-full text-xs font-semibold tracking-wide uppercase ${
          type === 'trending'
            ? 'bg-purple-50 text-purple-900 border border-purple-100'
            : 'bg-[#D4AF37]/10 text-amber-900 border border-[#D4AF37]/20'
        }`}
        style={{ fontFamily: "Montserrat, serif" }}
      >
        {type}
      </span>
    </div>
  );
};

export default function TrendingProductsSection() {
  const [products, setProducts] = useState([]); 
  const [loading, setLoading] = useState(true); 
  const [error, setError] = useState(null); 

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/list-featured`);
        if (!response.ok) {
          throw new Error('Failed to fetch featured products');
        }
        const data = await response.json();
        setProducts(data.featured_products); 
      } catch (err) {
        setError(err.message); 
      } finally {
        setLoading(false); 
      }
    };

    fetchFeaturedProducts(); 
  }, []);

  // Carousel settings - dynamically adjust based on product count
  const settings = {
    dots: true,
    infinite: products.length > 3, // Only infinite if more than 3 products
    speed: 500,
    slidesToShow: Math.min(products.length, 3), // Show max 3 or less if fewer products
    slidesToScroll: 1,
    centerMode: products.length === 1, // Center mode only for single product
    centerPadding: products.length === 1 ? '0px' : '60px',
    autoplay: products.length > 1, // Only autoplay if more than 1 product
    autoplaySpeed: 3000, 
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: Math.min(products.length, 2),
          slidesToScroll: 1,
          centerPadding: products.length === 1 ? '0px' : '40px',
          centerMode: products.length === 1,
          infinite: products.length > 2,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          centerPadding: '20px',
          centerMode: true,
          infinite: products.length > 1,
        },
      },
    ],
  };

  return (
    <section className="py-16 px-4 md:px-8 max-w-7xl mx-auto bg-gradient-to-b from-gray-50 to-white">
      {/* Section Header */}
      <div className="text-center mb-12">
        <h2 className="text-2xl md:text-4xl text-[#333333] mb-3 tracking-wide" style={{ fontFamily: "Losta Masta, serif" }}>
          Our Top Picks
        </h2>
        <p className="text-sm md:text-base text-gray-500 max-w-md mx-auto" style={{ fontFamily: "Montserrat, serif" }}>
          Discover the fragrances that are trending and loved by all.
        </p>
        <div className="mt-4 h-1 w-16 bg-[#D4AF37] mx-auto rounded-full"></div>
      </div>

      {/* Logic Branching: Loading vs Error/Empty vs Content */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
           <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37]"></div>
        </div>
      ) : error || products.length === 0 ? (
        // Friendly Message instead of Error
        <div className="flex justify-center items-center h-64 w-full bg-gray-50/50 rounded-xl border border-[#D4AF37]/10">
          <div className="text-center">
              <p className="text-gray-400 text-lg font-medium tracking-wide" style={{ fontFamily: "Montserrat, serif" }}>
                  No featured products at the moment.
              </p>
              <p className="text-gray-300 text-sm mt-2">Explore our collections for more!</p>
          </div>
        </div>
      ) : (
        // Content - Use Slider only if more than 1 product, otherwise simple display
        <div className="py-4">
          {products.length === 1 ? (
            // Single product - no slider needed
            <div className="flex justify-center">
              <TrendingProduct
                image={products[0].image}
                type={products[0].type}
              />
            </div>
          ) : (
            // Multiple products - use slider
            <Slider {...settings}>
              {products.map((product) => (
                <TrendingProduct
                  key={product._id} // Use _id from MongoDB for unique key
                  image={product.image}
                  type={product.type}
                />
              ))}
            </Slider>
          )}
        </div>
      )}
    </section>
  );
}